/**
 * useImageFetchingLight - 軽量版画像取得用カスタムフック
 * fetchAllImagesLight API専用。URL文字列ベースの遅延ローディング対応
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { validateRoomId } from '../../utils/validation/roomValidation';

// 軽量版APIレスポンス型
export interface ImageLightData {
  pictureId: number;
  imageUrl: string;
  thumbnailUrl: string;
  uploadDate: string;
  isModerated: number; // 0=未検閲, 1=承認済み, 2=拒否済み
  likesCount: number;
  isLikedByCurrentUser: boolean;
  selected?: boolean; // フロントエンド用状態
}

export interface ImageLightResponse {
  success: boolean;
  images: ImageLightData[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  hasNext: boolean;
  moderationFilter: string;
  sort: string;
  errors?: Array<{
    error: string;
    retryable: boolean;
  }>;
}

export interface ImageLightError {
  type: 'roomId' | 'network' | 'server' | 'partial' | 'unknown';
  message: string;
  details?: string;
  retryable: boolean;
  statusCode?: number;
}

interface UseImageFetchingLightOptions {
  urlBase: string;
  onError?: (error: ImageLightError) => void;
  onSuccess?: (response: ImageLightResponse) => void;
}

interface UseImageFetchingLightReturn {
  fetchImagesLight: (options?: {
    page?: number;
    size?: number;
    moderationFilter?: 'all' | 'unmoderated' | 'approved' | 'rejected';
    sort?: 'recent' | 'popular';
    guestUserId?: string;
  }) => Promise<ImageLightResponse>;
  loading: boolean;
  cancelFetching: () => void;
  isRequestActive: boolean;
}

/**
 * 軽量版画像取得用カスタムフック
 * fetchAllImagesLight APIを使用して、URL文字列ベースの高速レスポンスを提供
 */
export const useImageFetchingLight = (options: UseImageFetchingLightOptions): UseImageFetchingLightReturn => {
  const { urlBase, onError, onSuccess } = options;
  
  const [loading, setLoading] = useState(false);
  const [isRequestActive, setIsRequestActive] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);
  const lastRequestTimeRef = useRef<number>(0);
  
  // デバウンス設定
  const DEBOUNCE_MS = 100;

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * 現在のリクエストをキャンセルする
   */
  const cancelFetching = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('[useImageFetchingLight] Cancelling current request');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
      setIsRequestActive(false);
    }
  }, []);

  /**
   * APIエラーを分類して適切なエラーオブジェクトを生成
   */
  const classifyError = useCallback((error: any): ImageLightError => {
    if (axios.isCancel(error)) {
      return {
        type: 'unknown',
        message: 'リクエストがキャンセルされました',
        retryable: false
      };
    }

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const responseData = error.response?.data as any;
      const message = responseData?.message || error.message;
      
      switch (status) {
        case 401:
          return {
            type: 'server',
            message: '認証エラーが発生しました',
            details: 'ログインしなおしてください',
            retryable: false,
            statusCode: status
          };
        case 404:
          return {
            type: 'server',
            message: 'ルームが見つかりません',
            details: 'ルームIDを確認してください',
            retryable: false,
            statusCode: status
          };
        case 500:
          return {
            type: 'server',
            message: 'サーバーエラーが発生しました',
            details: 'しばらく時間をおいて再試行してください',
            retryable: true,
            statusCode: status
          };
        case undefined:
        case 0:
          return {
            type: 'network',
            message: 'ネットワークエラーが発生しました',
            details: 'インターネット接続を確認してください',
            retryable: true
          };
        default:
          return {
            type: 'server',
            message: '画像の読み込みに失敗しました',
            details: `エラーコード: ${status} - ${message}`,
            retryable: true,
            statusCode: status
          };
      }
    }

    return {
      type: 'unknown',
      message: '予期しないエラーが発生しました',
      details: error instanceof Error ? error.message : '不明なエラー',
      retryable: true
    };
  }, []);

  /**
   * 軽量版画像データを取得する
   */
  const fetchImagesLight = useCallback(async (fetchOptions: {
    page?: number;
    size?: number;
    moderationFilter?: 'all' | 'unmoderated' | 'approved' | 'rejected';
    sort?: 'recent' | 'popular';
    guestUserId?: string;
  } = {}): Promise<ImageLightResponse> => {
    console.log('[useImageFetchingLight] Starting fetchImagesLight');
    
    // デフォルト値設定
    const {
      page = 0,
      size = 16,
      moderationFilter = 'all',
      sort = 'recent',
      guestUserId
    } = fetchOptions;
    
    // デバウンス処理
    const now = Date.now();
    if (now - lastRequestTimeRef.current < DEBOUNCE_MS) {
      console.log('[useImageFetchingLight] Request debounced');
      return {
        success: false,
        images: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: page,
        hasNext: false,
        moderationFilter,
        sort,
        errors: [{ error: 'Request debounced', retryable: true }]
      };
    }
    lastRequestTimeRef.current = now;

    // 既存リクエストのキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 新しいAbortControllerを作成
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const currentRequestId = ++requestIdRef.current;

    // roomId検証
    const validation = validateRoomId();
    if (!validation.isValid) {
      const error: ImageLightError = {
        type: 'roomId',
        message: validation.error!,
        details: 'ルーム参加画面からやり直してください',
        retryable: false
      };
      
      if (onError) {
        onError(error);
      }
      
      return {
        success: false,
        images: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: page,
        hasNext: false,
        moderationFilter,
        sort,
        errors: [{ error: error.message, retryable: error.retryable }]
      };
    }

    const roomId = validation.roomId!;
    setLoading(true);
    setIsRequestActive(true);


    try {
      // クエリパラメータ構築
      const params = new URLSearchParams({
        roomId,
        page: page.toString(),
        size: size.toString(),
        moderationFilter,
        sort
      });
      
      if (guestUserId) {
        params.append('guestUserId', guestUserId);
      }

      const fullApiUrl = `${urlBase}/api/picture/fetchAllImagesLight?${params.toString()}`;
      console.log('[useImageFetchingLight] Making API request to:', fullApiUrl);
      
      const response = await axios.get(fullApiUrl, { 
        signal: controller.signal,
        timeout: 30000
      });
      
      console.log('[useImageFetchingLight] API response received:', {
        status: response.status,
        totalElements: response.data.totalElements,
        imagesCount: response.data.images?.length || 0
      });

      // リクエストがキャンセルされていないかチェック
      if (controller.signal.aborted) {
        throw new axios.Cancel('Request cancelled');
      }

      // 最新のリクエストかどうかチェック
      if (currentRequestId !== requestIdRef.current) {
        return {
          success: false,
          images: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: page,
          hasNext: false,
          moderationFilter,
          sort,
          errors: [{ error: 'Outdated request', retryable: false }]
        };
      }

      // レスポンスデータの変換
      const images: ImageLightData[] = (response.data.images || []).map((img: any) => ({
        pictureId: img.pictureId,
        imageUrl: img.imageUrl,
        thumbnailUrl: img.thumbnailUrl,
        uploadDate: img.uploadDate,
        isModerated: img.isModerated,
        likesCount: img.likesCount,
        isLikedByCurrentUser: img.isLikedByCurrentUser,
        selected: false
      }));

      const result: ImageLightResponse = {
        success: true,
        images,
        totalElements: response.data.totalElements || 0,
        totalPages: response.data.totalPages || 0,
        currentPage: response.data.currentPage || page,
        hasNext: response.data.hasNext || false,
        moderationFilter: response.data.moderationFilter || moderationFilter,
        sort: response.data.sort || sort
      };
      

      // 成功コールバック実行
      if (onSuccess) {
        onSuccess(result);
      }

      return result;

    } catch (error) {
      // リクエストがキャンセルされた場合
      if (axios.isCancel(error)) {
        throw error;
      }

      const classifiedError = classifyError(error);
      
      if (onError) {
        onError(classifiedError);
      }

      return {
        success: false,
        images: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: page,
        hasNext: false,
        moderationFilter,
        sort,
        errors: [{ 
          error: classifiedError.message, 
          retryable: classifiedError.retryable 
        }]
      };

    } finally {
      // 現在のリクエストの場合のみ状態をクリア
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        setIsRequestActive(false);
        
        // AbortControllerをクリア
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    }
  }, [urlBase, onError, onSuccess, classifyError]);

  return {
    fetchImagesLight,
    loading,
    cancelFetching,
    isRequestActive
  };
};